import { useEffect } from "react";
import type { RefObject } from "react";
import type { AnimationNodeKind } from "@/document/model/animation";
import type { Paint } from "@/document/model/styles";
import { findActionForKeyboardEvent, isEditableShortcutTarget, type ShortcutAction } from "@/editor/actions/keymap";
import { animationNodeKindForHotkey } from "@/editor/shell/shellAnimationHotkeys";
import { clipboardStore } from "@/editor/store/clipboardStore";
import { uiStore } from "@/editor/store/uiStore";
import { nativeAssetPasteAction, readImageClipboardFromDataTransfer, readSvgFromDataTransfer } from "@/editor/shell/shellTransfer";
import type { EditorMode } from "@/editor/store/editorStoreTypes";

export interface EditorShellKeymapOptions {
  rootRef: RefObject<HTMLElement | null>;
  isDocumentLoading: boolean;
  prototypePlayerFrameId: string | undefined;
  scriptPreviewActive: boolean;
  animationPanelOpen: boolean;
  animationTimelineOpen: boolean;
  armedAnimationNodeKind: AnimationNodeKind | undefined;
  editorActions: ShortcutAction[];
  canPasteVectorVertices: () => boolean;
  setAnimationModeOpen: (open: boolean, recordHistory: boolean) => void;
  setAnimationTimelineOpenPreference: (open: boolean) => void;
  resetAnimationPlayback: () => void;
}

export function bindEditorShellKeymap(options: EditorShellKeymapOptions): () => void {
  const handleKeyDown = (event: KeyboardEvent) => {
    if (
      options.isDocumentLoading
      || options.prototypePlayerFrameId
      || options.scriptPreviewActive
      || !options.rootRef.current
      || isEditableShortcutTarget(event.target)
    ) {
      return;
    }

    if ((event.key === "a" || event.key === "A") && (event.metaKey || event.ctrlKey) && event.shiftKey) {
      event.preventDefault();
      options.setAnimationModeOpen(!options.animationPanelOpen, true);
      return;
    }

    if (options.animationPanelOpen) {
      if (event.key === "Escape" && options.armedAnimationNodeKind) {
        event.preventDefault();
        uiStore.patchAnimation({ armedNodeKind: undefined });
        return;
      }
      if ((event.key === "t" || event.key === "T") && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        options.setAnimationTimelineOpenPreference(!options.animationTimelineOpen);
        return;
      }
      const animationNodeKind = animationNodeKindForHotkey(event);
      if (animationNodeKind) {
        event.preventDefault();
        const armedKind = uiStore.animation.getValue().armedNodeKind;
        uiStore.patchAnimation({ armedNodeKind: armedKind === animationNodeKind ? undefined : animationNodeKind });
        return;
      }
    }

    if (options.animationPanelOpen && event.key === "Enter" && event.shiftKey && !event.metaKey && !event.ctrlKey && !event.altKey) {
      event.preventDefault();
      options.resetAnimationPlayback();
      return;
    }

    const action = findActionForKeyboardEvent(options.editorActions, event);
    if (!action) {
      return;
    }

    const isCopyOrCut = action.id === "edit.copySelection" || action.id === "edit.cutSelection";
    if (isCopyOrCut && window.getSelection()?.type === "Range") {
      return;
    }

    const isPasteAction = action.id === "edit.pasteSelection";
    if (!isPasteAction || options.canPasteVectorVertices()) {
      event.preventDefault();
    }
    action.run(event);
  };

  window.addEventListener("keydown", handleKeyDown);
  return () => window.removeEventListener("keydown", handleKeyDown);
}

export interface AnimationPlaybackKeymapOptions {
  animationPanelOpen: boolean;
  animationPlaying: boolean;
  animationTimeMs: number;
  animationDurationMs: number;
  animationLoopRegion: { startMs: number; endMs: number } | undefined;
  pauseAnimation: () => void;
  resetAnimationPlayback: () => void;
  /** When true, Space/Shift+Enter must not steal keys from the script preview. */
  scriptPreviewActive?: boolean;
}

export function bindAnimationPlaybackKeymap(options: AnimationPlaybackKeymapOptions): () => void {
  const toggleAnimationPlayback = () => {
    if (options.animationPlaying) {
      options.pauseAnimation();
      return;
    }
    if (options.animationDurationMs <= 0) {
      return;
    }
    uiStore.patchAnimation({ playing: true });
    if (
      options.animationLoopRegion &&
      options.animationLoopRegion.endMs > options.animationLoopRegion.startMs &&
      (options.animationTimeMs < options.animationLoopRegion.startMs || options.animationTimeMs >= options.animationLoopRegion.endMs)
    ) {
      uiStore.patchAnimation({ timeMs: Math.max(0, Math.min(options.animationLoopRegion.startMs, options.animationDurationMs)) });
    } else if (options.animationTimeMs >= options.animationDurationMs) {
      uiStore.patchAnimation({ timeMs: 0 });
    }
  };

  const handleAnimationSpaceKey = (event: KeyboardEvent) => {
    if (options.scriptPreviewActive) {
      return;
    }
    const isSpace = event.code === "Space" || event.key === " ";
    if (!isSpace) {
      return;
    }
    if (isEditableShortcutTarget(event.target)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    if (globalThis.document.activeElement instanceof HTMLElement) {
      globalThis.document.activeElement.blur();
    }
    toggleAnimationPlayback();
  };

  const handleAnimationEnterKey = (event: KeyboardEvent) => {
    if (options.scriptPreviewActive) {
      return;
    }
    const isEnter = event.key === "Enter";
    if (!isEnter || !event.shiftKey) {
      return;
    }
    if (isEditableShortcutTarget(event.target)) {
      return;
    }
    event.preventDefault();
    options.resetAnimationPlayback();
  };

  window.addEventListener("keydown", handleAnimationSpaceKey, { capture: true });
  window.addEventListener("keydown", handleAnimationEnterKey);
  return () => {
    window.removeEventListener("keydown", handleAnimationSpaceKey, { capture: true });
    window.removeEventListener("keydown", handleAnimationEnterKey);
  };
}

export interface EditorShellClipboardOptions {
  rootRef: RefObject<HTMLElement | null>;
  isDocumentLoading: boolean;
  mode: EditorMode;
  animationPanelOpen: boolean;
  canPasteVectorVertices: () => boolean;
  pasteVectorVerticesFromClipboard: () => boolean;
  pasteSvg: (svgMarkup: string, insertPoint?: { x: number; y: number }) => void;
  pasteImage: (imagePaint: Extract<Paint, { type: "image" }>, insertPoint?: { x: number; y: number }) => void;
}

export function bindEditorShellClipboard(options: EditorShellClipboardOptions): () => void {
  const handlePaste = (event: ClipboardEvent) => {
    if (options.isDocumentLoading || !options.rootRef.current || isEditableShortcutTarget(event.target) || options.mode === "textEdit") {
      return;
    }

    if (options.canPasteVectorVertices()) {
      event.preventDefault();
      options.pasteVectorVerticesFromClipboard();
      return;
    }

    const plainSvg = event.clipboardData?.getData("text/plain");
    const assetPasteAction = nativeAssetPasteAction(options.animationPanelOpen, event.clipboardData?.items ?? [], plainSvg);
    if (assetPasteAction === "ignore") {
      return;
    }

    if (assetPasteAction === "block") {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    void readSvgFromDataTransfer(event.clipboardData).then(svgMarkup => {
      if (svgMarkup) {
        clipboardStore.markImagePasteHandled();
        options.pasteSvg(svgMarkup);
        return;
      }
      void readImageClipboardFromDataTransfer(event.clipboardData).then(imagePaint => {
        if (!imagePaint) {
          return;
        }
        clipboardStore.markImagePasteHandled();
        options.pasteImage(imagePaint);
      });
    });
  };

  window.addEventListener("paste", handlePaste);
  return () => window.removeEventListener("paste", handlePaste);
}

export function useEditorShellKeymap(options: EditorShellKeymapOptions): void {
  useEffect(() => bindEditorShellKeymap(options), [
    options.animationPanelOpen,
    options.animationTimelineOpen,
    options.armedAnimationNodeKind,
    options.editorActions,
    options.isDocumentLoading,
    options.prototypePlayerFrameId,
    options.scriptPreviewActive,
    options.resetAnimationPlayback,
    options.setAnimationModeOpen,
    options.setAnimationTimelineOpenPreference,
  ]);
}

export function useAnimationPlaybackKeymap(options: AnimationPlaybackKeymapOptions): void {
  useEffect(() => {
    if (!options.animationPanelOpen) {
      return;
    }
    return bindAnimationPlaybackKeymap(options);
  }, [
    options.animationDurationMs,
    options.animationLoopRegion,
    options.animationPanelOpen,
    options.animationPlaying,
    options.animationTimeMs,
    options.pauseAnimation,
    options.resetAnimationPlayback,
  ]);
}

export function useEditorShellClipboard(options: EditorShellClipboardOptions): void {
  useEffect(() => bindEditorShellClipboard(options), [
    options.animationPanelOpen,
    options.isDocumentLoading,
    options.mode,
    options.canPasteVectorVertices,
    options.pasteVectorVerticesFromClipboard,
    options.pasteSvg,
    options.pasteImage,
  ]);
}
